import React, { useState, useEffect } from "react";
import { Box, DialogContent, Grid } from "@mui/material";
import TextButtonComponet from "../../atom/Buttons/TextButton";
import TextInputComponent from "../../atom/Inputs/TextInput";
import ThumbnailUploadComponent from "../../atom/Inputs/Thumbnail";

const AdminDocumentEdit = ({ documentData, onClose, updateDocument, baseUrl }) => {
  const isFolder = Number(documentData?.is_directory) === 1;

  const [name, setName] = useState("");
  const [fileExtension, setFileExtension] = useState("");
  const [fileValue, setFileValue] = useState(null);
  const [thumbnailValue, setThumbnailValue] = useState(null);
  const [thumbnailRemoved, setThumbnailRemoved] = useState(false);
  const [errors, setErrors] = useState({});

  const urlToFile = async (url, filename) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const mimeType = blob.type || "application/octet-stream";
    return new File([blob], filename, { type: mimeType });
  };

  useEffect(() => {
    const fileName = documentData?.name || "";
    const lastDotIndex = fileName.lastIndexOf(".");

    if (lastDotIndex > 0) {
      setName(fileName.substring(0, lastDotIndex));
      setFileExtension(fileName.substring(lastDotIndex));
    } else {
      setName(fileName);
      setFileExtension("");
    }

    let initialFilePath = documentData?.path || documentData?.file_url || null;
    if (typeof initialFilePath === "string" && initialFilePath && !initialFilePath.startsWith("http")) {
      const cleanedBaseUrl = baseUrl ? baseUrl.replace(/\/+$/, "") : "";
      const cleanedPath = initialFilePath.replace(/^\/+/, "");
      initialFilePath = cleanedBaseUrl ? `${cleanedBaseUrl}/${cleanedPath}` : initialFilePath;
    }
    setFileValue(initialFilePath);

    let initialThumbnail = documentData?.thumbnail || documentData?.thumbnail_url || null;
    if (typeof initialThumbnail === "string" && initialThumbnail && !initialThumbnail.startsWith("http")) {
      const cleanedBaseUrl = baseUrl ? baseUrl.replace(/\/+$/, "") : "";
      const cleanedPath = initialThumbnail.replace(/^\/+/, "");
      initialThumbnail = cleanedBaseUrl ? `${cleanedBaseUrl}/${cleanedPath}` : initialThumbnail;
    }

    setThumbnailValue(initialThumbnail);
    setThumbnailRemoved(false);
    setErrors({});
  }, [documentData, baseUrl]);

  const handleDocumentChange = (selectedFile) => {
    setFileValue(selectedFile);
    if (selectedFile) {
      setErrors((prev) => ({ ...prev, file: null }));

      if (selectedFile instanceof File) {
        const fullFileName = selectedFile.name || "";
        const lastDotIndex = fullFileName.lastIndexOf(".");
        if (lastDotIndex > 0) {
          setFileExtension(fullFileName.substring(lastDotIndex));
        }
      }
    }
  };

  const handleThumbnailChange = (newValue) => {
    setThumbnailValue(newValue);
    if (!newValue) {
      setThumbnailRemoved(true);
    } else {
      setThumbnailRemoved(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (name.includes(".")) {
      newErrors.name = "Document name can not contain dots (.)";
    }

    if (!isFolder && !fileValue) {
      newErrors.file = "Please select a document file";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getNameWithoutExtension = (value) => {
    if (!value) return "";
    const rawValue = String(value).split(/[\\/]/).pop() || "";
    const lastDotIndex = rawValue.lastIndexOf(".");
    if (lastDotIndex <= 0) return rawValue;
    return rawValue.substring(0, lastDotIndex);
  };

  const handleUpdate = async () => {
    if (!documentData?.id) {
      onClose();
      return;
    }

    if (!validate()) return;

    const fileBasedName = getNameWithoutExtension(
      fileValue?.name || fileValue || documentData?.path || documentData?.name || ""
    );
    const fallbackName = getNameWithoutExtension(documentData?.name || documentData?.path || "");
    const finalName = name.trim() || fileBasedName || fallbackName || "Untitled";
    const fd = new FormData();
    fd.append("id", documentData.id);
    fd.append("name", isFolder ? finalName : finalName);

    if (isFolder) {
      fd.append("is_directory", 1);
      if (documentData?.course_id) fd.append("course_id", documentData.course_id);
      if (documentData?.parent_id) fd.append("parent_id", documentData.parent_id);
    } else {
      if (fileValue instanceof File || fileValue instanceof Blob) {
        fd.append("document", fileValue);
      }
    }

    // Process Thumbnail Logic
    let thumbnailToSend = thumbnailValue;
    if (thumbnailRemoved || !thumbnailValue) {
      fd.append("delete_thumbnail", 1);
    } else if (thumbnailToSend instanceof File || thumbnailToSend instanceof Blob) {
      fd.append("thumbnail", thumbnailToSend);
      fd.append("delete_thumbnail", 0);
    } else {
      // If thumbnail is unchanged URL string, keep delete_thumbnail 0
      fd.append("delete_thumbnail", 0);
    }

    if (updateDocument) {
      await updateDocument({ document_id: documentData.id, fd });
    }

    onClose();
  };

  return (
    <DialogContent>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextInputComponent
            label={"Name"}
            placeholder="Document name"
            isError={Boolean(errors.name)}
            error={errors.name}
            value={name}
            onchange={(e) => {
              const value = e.target.value;
              setName(value);
              setErrors((prev) => ({
                ...prev,
                name: value.includes(".")
                  ? "Document name can not contain dots (.)"
                  : null,
              }));
            }}
            fullWidth
          />
        </Grid>

        {!isFolder && (
          <Grid item xs={12}>
            <ThumbnailUploadComponent
              label="Document"
              description="PDF, PNG, JPG, JPEG etc."
              accept={[".pdf", ".png", ".jpg", ".jpeg"]}
              value={fileValue}
              onChange={handleDocumentChange}
              required={true}
              isError={Boolean(errors.file)}
              error={errors.file}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <ThumbnailUploadComponent
            label="Thumbnail"
            description="PNG, JPG, JPEG up to 2MB"
            accept={[".jpg", ".jpeg", ".png"]}
            value={thumbnailValue}
            onChange={handleThumbnailChange}
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 2,
              gap: 2,
            }}
          >
            <TextButtonComponet
              text="Cancel"
              classStyle="btn btn-secondary"
              onButtonClick={onClose}
            />

            <TextButtonComponet
              text="Update"
              onButtonClick={handleUpdate}
            />
          </Box>
        </Grid>
      </Grid>
    </DialogContent>
  );
};

export default AdminDocumentEdit;